import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
//
//MUI Components
import { makeStyles } from "@material-ui/core/styles";
import { DataGrid, GridToolbar } from "@material-ui/data-grid";
import Typography from "@material-ui/core/Typography";
import { Button, Tooltip } from "@material-ui/core";
import { IconButton } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";

const useStyles = makeStyles((theme) => ({
	datagrid: {
		backgroundColor: "white",
		// Remove outlines because they're gross
		"&.MuiDataGrid-root .MuiDataGrid-cell:focus": {
			outline: "none",
		},
	},
	slugLink: {
		color: theme.palette.primary.main,
	},
}));

export default function Sites() {
	const [rows, setRows] = useState([]);
	const [selectionModel, setSelectionModel] = useState([]);
	const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);

	const classes = useStyles();

	useEffect(() => {
		const fetchRows = async () => {
			const res = await fetch(`${process.env.REACT_APP_API_URL}/sites`);
			const data = await res.json();
			return data;
		};

		const getRows = async () => {
			let rowsFromServer = await fetchRows();
			setRows(rowsFromServer.data);
		};
		getRows();
	}, []);

	const deleteFromAPI = async (id) => {
		try {
			await fetch(`${process.env.REACT_APP_API_URL}/sites/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
		} catch (e) {
			console.log(e);
		}
	};

	const onClickDeleteAll = async () => {
		let confirmDelete = window.prompt(
			'Deleting a site will delete all of its contents. Please enter "DELETE" below to confirm deleting the selected sites.'
		);

		// Confirm before continuing
		if (confirmDelete && confirmDelete == "DELETE") {
			selectionModel.forEach((id) => {
				deleteFromAPI(id);
			});

			//Remove selectionModeled items from the rows state
			let newRows = [...rows];
			for (let i = 0; i < selectionModel.length; i++) {
				newRows = newRows.filter((row) => row._id !== selectionModel[i]);
			}
			await setRows(newRows);

			//Clear selection
			setSelectionModel([]);
		} else if (confirmDelete && confirmDelete !== "DELETE") {
			window.alert('"DELETE" must be typed in all caps. Please try again.');
		}
	};

	const columns = useMemo(
		() => [
			{ field: "_id", headerName: "ID", hide: true },
			{
				field: "name",
				headerName: "Name",
				renderCell: (params) => {
					return (
						<React.Fragment>
							<IconButton
								component={Link}
								to={`/${process.env.REACT_APP_ADMIN_URL}/sites/${params.row._id}`}
								style={{ marginRight: ".5rem" }}
								aria-label="edit"
							>
								<EditIcon />
							</IconButton>
							<Tooltip title={params.value} placement="bottom-start">
								<p>{params.value}</p>
							</Tooltip>
						</React.Fragment>
					);
				},
				flex: 2,
			},
			{
				field: "published",
				headerName: "Status",
				valueGetter: (params) => {
					if (params.value) {
						return "Published";
					} else {
						return "Draft";
					}
				},
				renderCell: (params) => {
					if (params.value === "Published") {
						return (
							<Typography variant="body2" color="primary">
								<strong>{params.value}</strong>
							</Typography>
						);
					} else {
						return (
							<Typography variant="body2" color="secondary">
								<strong>{params.value}</strong>
							</Typography>
						);
					}
				},
				flex: 1,
			},
			{
				field: "slug",
				headerName: "Slug",
				flex: 1,
				renderCell: (params) => {
					return (
						<Tooltip title={`/${params.row.slug}`} placement="bottom-start">
							<Typography component="span" variant="body2">
								<Link
									className={classes.slugLink}
									to={`/${params.row.slug}`}
									target="_blank"
								>
									{params.row.slug}
								</Link>
							</Typography>
						</Tooltip>
					);
				},
			},
			{
				field: "units",
				headerName: "Units",
				renderCell: (params) => params.value.length || "0",
				flex: 1,
				type: "number",
			},
		],
		[classes.slugLink]
	);

	return (
		<>
			<div
				style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}
			>
				<Typography component="h1" variant="h4">
					Sites
				</Typography>
				<Button
					startIcon={<AddIcon />}
					style={{ marginLeft: "1rem" }}
					color="primary"
					variant="contained"
					component={Link}
					to={`/${process.env.REACT_APP_ADMIN_URL}/sites/add`}
				>
					Add New
				</Button>
				{selectionModel.length > 0 && (
					<Button
						startIcon={<DeleteIcon />}
						style={{ marginLeft: "1rem" }}
						color="secondary"
						variant="contained"
						onClick={onClickDeleteAll}
					>
						Delete
					</Button>
				)}
			</div>
			<DataGrid
				className={classes.datagrid}
				components={{
					Toolbar: GridToolbar,
				}}
				getRowId={(row) => row._id}
				autoHeight
				rows={rows}
				columns={columns}
				checkboxSelection
				disableSelectionOnClick
				selectionModel={selectionModel}
				sortModel={sortModel}
				onSortModelChange={(model) => setSortModel(model)}
				onSelectionModelChange={(newSelection) => {
					setSelectionModel(newSelection);
				}}
			/>
		</>
	);
}
